import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Box, Center, Spinner, Text } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  type Column,
  type StatusType,
  type ToggleOption,
} from '@themes/aegix';
import { useReunificationEvents } from '../../hooks/queries/useReunification';
import { useAlertHistory } from '../../hooks/queries/useAlerts';
import { useIncidents } from '../../hooks/queries/useIncidents';
import { useReunificationJoinPrompt } from '../../contexts/ReunificationJoinPromptContext';
import {
  accountedFromStats,
  type ReunificationEvent,
} from '../../types/ReunificationTypes';
import type { AlertHistory } from '../../services/alerts.service';
import type { Incident } from '../../services/incidents.service';
import { EventDashboardStats } from './EventDashboardStats';
import { EventDetailDrawer } from './drawers/EventDetailDrawer';

// toggleOptions and columns are built inside EventsOverview (useMemo)
// so their labels re-evaluate whenever the language changes.

interface EventRow {
  [key: string]: unknown;
  id: string;
  type: string;
  type_badge: 'alert' | 'reunification' | 'drill' | 'incident';
  school: string;
  details: string;
  status: StatusType;
  status_label: string;
  started: string;
  started_raw: string | null;
  original_alert_id?: number;
}

function mapAlertHistoryStatus(alert: AlertHistory): StatusType {
  if (alert.pending_approval) return 'pending';
  if (alert.cancelled) return 'inactive';
  return 'active';
}

function mapAlertHistoryStatusLabel(alert: AlertHistory): string {
  if (alert.pending_approval) return 'Pending';
  if (alert.cancelled) return 'Ended';
  return 'Active';
}

function mapReunificationStatus(status: string): StatusType {
  const statusMap: Record<string, StatusType> = {
    pending: 'pending',
    active: 'active',
    paused: 'pending',
    completed: 'active',
    cancelled: 'inactive',
  };
  return statusMap[status] ?? 'pending';
}

function transformAlertHistory(alerts: AlertHistory[]): EventRow[] {
  return alerts.map((alert) => ({
    id: `alert-${alert.id}`,
    type: alert.internal ? 'Drill' : 'Alert',
    type_badge: alert.internal ? ('drill' as const) : ('alert' as const),
    school: alert.site_name,
    details: [
      alert.alert_type_name,
      `Police: ${alert.last_police_status}`,
      alert.silent ? 'Silent' : null,
      alert.scoped ? 'Scoped' : null,
    ]
      .filter(Boolean)
      .join(' · '),
    status: mapAlertHistoryStatus(alert),
    status_label: mapAlertHistoryStatusLabel(alert),
    started: new Date(alert.created).toLocaleString(),
    started_raw: alert.created,
    original_alert_id: alert.original_alert_id,
  }));
}

function transformReunificationEvents(
  events: ReunificationEvent[],
): EventRow[] {
  return events.map((event) => ({
    id: `reun-${event.public_id}`,
    type: event.is_drill ? 'Drill' : 'Reunify',
    type_badge: event.is_drill
      ? ('drill' as const)
      : ('reunification' as const),
    school: event.school_name,
    details: `${event.incident_type_display} · ${event.plan_name ?? 'No plan'} · ${accountedFromStats(event.stats)}/${event.stats.total_students} accounted`,
    status: mapReunificationStatus(event.status),
    status_label: event.status_display,
    started: event.started_at
      ? new Date(event.started_at).toLocaleString()
      : '—',
    started_raw: event.started_at ?? null,
  }));
}

function transformIncidents(incidents: Incident[]): EventRow[] {
  return incidents.map((incident) => ({
    id: `incident-${incident.id}`,
    type: 'Incident',
    type_badge: 'incident' as const,
    school: `School #${incident.school}`,
    details: `${incident.type_name}${incident.reference_id ? ` · ${incident.reference_id}` : ''}`,
    status: 'active' as StatusType,
    status_label: 'Active',
    started: new Date(incident.created).toLocaleString(),
    started_raw: incident.created,
  }));
}

// Pulls the same aegix semantic tokens AIM's Start Alert / Start Drill /
// Start Report / Reunify buttons use, so the events table matches AIM's
// color taxonomy exactly:
//   red (#E70E00) / blue (#0A84FF) / yellow.600 (#D6AC00) / purple (#9737A8).
const typeBadgeBg: Record<string, string> = {
  alert: 'surface.danger',
  drill: 'surface.drill',
  incident: 'surface.report',
  reunification: 'surface.reunification',
};

// Shared dimensions/styling so the type pill renders as a fixed-size
// box with the label centered horizontally and vertically. The status
// column uses the dot+colored-text StatusBadge instead.
const BADGE_PILL_PROPS = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center' as const,
  color: 'white',
  fontWeight: 'bold',
  fontSize: 'md',
  borderRadius: 'md',
  w: '96px',
  h: '28px',
  px: 0,
};


export function EventsOverview() {
  const { t } = useTranslation();
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<{
    type: EventRow['type_badge'];
    id: string;
    isEnded: boolean;
    originalAlertId?: number;
  } | null>(null);

  // Cross-site "reunification started" Join prompt → auto-open drawer
  // for the event the user just joined. Consumed once on mount; the
  // context clears the pending link in the same call. Deep-linked events
  // are by definition just-activated, so isEnded=false.
  const { consumePendingDeepLink } = useReunificationJoinPrompt();
  useEffect(() => {
    const link = consumePendingDeepLink();
    if (!link) return;
    setSelectedEvent({
      type: link.event_type,
      id: link.event_id,
      isEnded: false,
    });
    setDrawerOpen(true);
  }, [consumePendingDeepLink]);

  // Build toggle options and columns inside the component so labels are
  // reactive to language changes via useMemo([t]).
  const toggleOptions = useMemo<ToggleOption[]>(() => [
    { value: 'all', label: t('events.toggle.all', 'All'), 'data-e2e': 'event-filter-all' },
    { value: 'drills', label: t('events.toggle.drills', 'Drills'), 'data-e2e': 'event-filter-drills' },
    { value: 'incidents', label: t('events.toggle.incidents', 'Incidents'), 'data-e2e': 'event-filter-incidents' },
    { value: 'alerts', label: t('events.toggle.alerts', 'Alerts'), 'data-e2e': 'event-filter-alerts' },
    { value: 'reunifications', label: t('events.toggle.reunifications', 'Reunifications'), 'data-e2e': 'event-filter-reunifications' },
  ], [t]);

  const columns = useMemo<Column<EventRow>[]>(() => [
    {
      key: 'type',
      label: t('events.col.type', 'Type'),
      sortable: true,
      filterable: true,
      filterOptions: [
        { label: t('events.type.alert', 'Alert'), value: 'Alert' },
        { label: t('events.type.reunify', 'Reunify'), value: 'Reunify' },
        { label: t('events.type.drill', 'Drill'), value: 'Drill' },
      ],
      width: '140px',
      render: (row) => {
        const typeLabel =
          row.type_badge === 'alert'
            ? t('events.type.alert', 'Alert')
            : row.type_badge === 'drill'
              ? t('events.type.drill', 'Drill')
              : row.type_badge === 'reunification'
                ? t('events.type.reunify', 'Reunify')
                : t('events.type.incident', 'Incident');
        return (
          <Badge
            {...BADGE_PILL_PROPS}
            bg={typeBadgeBg[row.type_badge]}
            data-e2e={`event-type-${row.id}`}
          >
            {typeLabel}
          </Badge>
        );
      },
    },
    {
      key: 'school',
      label: t('events.col.school', 'School'),
      sortable: true,
      filterable: true,
      width: '200px',
    },
    { key: 'details', label: t('events.col.details', 'Details'), sortable: false, width: '280px' },
    {
      key: 'status',
      label: t('events.col.status', 'Status'),
      sortable: true,
      filterable: true,
      filterOptions: [
        { label: t('events.status.active', 'Active'), value: 'active' },
        { label: t('events.status.pending', 'Pending'), value: 'pending' },
        { label: t('events.status.ended', 'Ended'), value: 'inactive' },
      ],
      width: '120px',
      render: (row) => {
        const statusLabel =
          row.status === 'active'
            ? t('events.status.active', 'Active')
            : row.status === 'pending'
              ? t('events.status.pending', 'Pending')
              : t('events.status.ended', 'Ended');
        return <StatusBadge status={row.status} label={statusLabel} size="md" />;
      },
    },
    { key: 'started', label: t('events.col.started', 'Started'), sortable: true, width: '180px' },
  ], [t]);

  // URL deep link: `/events?event=<public_id>` (and optional
  // `?type=reunification|drill`, defaults to reunification). Used when
  // sister apps (AIM React, iOS, Android) link out via the cross-app
  // "Manage" button — the drawer's own detail fetch reconciles status
  // and the actual event type once mounted, so an incorrect default
  // here is self-healing.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eventId = searchParams.get('event');
    if (!eventId) return;
    const typeParam = searchParams.get('type');
    const eventType: EventRow['type_badge'] =
      typeParam === 'drill' ? 'drill' : 'reunification';
    setSelectedEvent({ type: eventType, id: eventId, isEnded: false });
    setDrawerOpen(true);
    // Strip params so back/forward doesn't re-open and a manual close
    // sticks. `replace: true` keeps history clean.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('event');
        next.delete('type');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const showAlerts = typeFilter === 'all' || typeFilter === 'alerts';
  const showDrills = typeFilter === 'all' || typeFilter === 'drills';
  const showAlertHistory = showAlerts || showDrills;
  const showReunifications =
    typeFilter === 'all' ||
    typeFilter === 'reunifications' ||
    typeFilter === 'drills';
  const showIncidents = typeFilter === 'all' || typeFilter === 'incidents';

  const ordering = sortColumn
    ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
    : undefined;

  const { data: alertHistoryData, isLoading: alertHistoryLoading } =
    useAlertHistory({
      page_size: 100,
      is_origin: 'true',
      sort: '-created',
    });

  const { data: reunificationData, isLoading: reunificationLoading } =
    useReunificationEvents({
      search: searchValue || undefined,
      is_drill: typeFilter === 'drills' ? true : undefined,
      page_size: 100,
      ordering,
    });

  const { data: incidentsData, isLoading: incidentsLoading } = useIncidents({
    page_size: 100,
    ordering,
  });

  // Stats query — fetches all events for EventDashboardStats
  const { data: statsData } = useReunificationEvents({
    page_size: 1000,
  });

  const isLoading =
    (showAlertHistory && alertHistoryLoading) ||
    (showReunifications && reunificationLoading) ||
    (showIncidents && incidentsLoading);

  const combinedEvents = useMemo(() => {
    const rows: EventRow[] = [];

    if (showAlertHistory && alertHistoryData?.results) {
      const filtered = alertHistoryData.results.filter((alert) => {
        if (typeFilter === 'alerts') return !alert.internal;
        if (typeFilter === 'drills') return alert.internal;
        return true;
      });
      rows.push(...transformAlertHistory(filtered));
    }

    if (showReunifications && reunificationData?.results) {
      const filteredEvents = reunificationData.results.filter((event) => {
        if (typeFilter === 'drills') return event.is_drill;
        if (typeFilter === 'reunifications') return !event.is_drill;
        return true;
      });
      rows.push(...transformReunificationEvents(filteredEvents));
    }

    if (showIncidents && incidentsData?.results) {
      rows.push(...transformIncidents(incidentsData.results));
    }

    let working = rows;
    if (searchValue) {
      const search = searchValue.toLowerCase();
      working = rows.filter(
        (row) =>
          row.school.toLowerCase().includes(search) ||
          row.details.toLowerCase().includes(search) ||
          row.type.toLowerCase().includes(search),
      );
    }

    const effectiveSortColumn = sortColumn ?? 'started';
    const effectiveSortDirection: 'asc' | 'desc' = sortDirection ?? 'desc';

    working.sort((a, b) => {
      let valA = a[effectiveSortColumn];
      let valB = b[effectiveSortColumn];

      if (effectiveSortColumn === 'started') {
        valA = a.started_raw;
        valB = b.started_raw;
        if (!valA && !valB) return 0;
        if (!valA) return effectiveSortDirection === 'asc' ? 1 : -1;
        if (!valB) return effectiveSortDirection === 'asc' ? -1 : 1;
        const timeA = new Date(valA as string).getTime();
        const timeB = new Date(valB as string).getTime();
        return effectiveSortDirection === 'asc' ? timeA - timeB : timeB - timeA;
      }

      if (typeof valA === 'string' && typeof valB === 'string') {
        return effectiveSortDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      if ((valA as number) < (valB as number)) return effectiveSortDirection === 'asc' ? -1 : 1;
      if ((valA as number) > (valB as number)) return effectiveSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return working;
  }, [
    showAlertHistory,
    showReunifications,
    showIncidents,
    alertHistoryData,
    reunificationData,
    incidentsData,
    typeFilter,
    searchValue,
    sortColumn,
    sortDirection,
  ]);

  const totalItems = combinedEvents.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedEvents = combinedEvents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const statsEvents = statsData?.results ?? [];

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, typeFilter, pageSize]);

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const filteredEvents = useMemo(() => {
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length === 0) return paginatedEvents;
    return paginatedEvents.filter((row) =>
      activeFilters.every(([col, val]) => {
        const cellValue = String(
          (row as Record<string, unknown>)[col] ?? '',
        ).toLowerCase();
        return cellValue.includes(val.toLowerCase());
      }),
    );
  }, [paginatedEvents, filterValues]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleRowClick = useCallback((row: EventRow) => {
    const dashIndex = row.id.indexOf('-');
    setSelectedEvent({
      type: row.type_badge,
      id: dashIndex !== -1 ? row.id.slice(dashIndex + 1) : row.id,
      // The events table already classifies status (active/inactive/pending);
      // the only state where Reunify makes sense is `active`. Anything else
      // (cancelled alerts, pending-approval alerts) blocks the action.
      isEnded: row.status !== 'active',
      originalAlertId: row.original_alert_id,
    });
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedEvent(null);
  };

  return (
    <>
      {/* Dashboard stats — responsive to current filters */}
      <Box px={5} pt={5} pb={2}>
        <EventDashboardStats events={statsEvents} />
      </Box>

      <TableOptions
        toggleOptions={toggleOptions}
        activeToggle={typeFilter}
        onToggleChange={(v: string) => {
          setTypeFilter(v);
          setCurrentPage(1);
        }}
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          setCurrentPage(1);
        }}
        data-e2e="EventsTableOptions"
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : paginatedEvents.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{t('events.noEventsFound', 'No events found')}</Text>
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredEvents}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={handleRowClick}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </Box>

      <EventDetailDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        eventId={selectedEvent?.id ?? ''}
        eventType={selectedEvent?.type ?? 'alert'}
        isEnded={selectedEvent?.isEnded ?? false}
        originalAlertId={selectedEvent?.originalAlertId}
      />
    </>
  );
}
